/**
 * useCheckpointTimeline — REQ-090 state machine for the admin Checkpoint
 * Timeline module (ODE-P07-T04).
 *
 * Flow: `idle` (no delivery order selected) → `submit()` → `loading` → the
 * guarded timeline outcome:
 *   200 with slots → `ready`   (server-derived CP1..CP7 timeline)
 *   200 with 0 slots → `empty` (explicit "no checkpoints", never fabricated)
 *   400 → `error`   (malformed DO id — retryable with corrected input)
 *   401 → `unauthorized`
 *   403 → `blocked` (server-enforced; the client mirror is UX only)
 *   404 → `not_found` (unknown delivery order)
 *   503/network → `error`
 *
 * Nothing is computed here: the REQ-012 slot states, the REQ-031 captured
 * GPS/timestamp, the REQ-076 actor/audit linkage and the REQ-073 authority
 * markers all come from the server projection verbatim.
 *
 * Traces to: ODE-PRD-REQ-045, ODE-PRD-REQ-012, ODE-PRD-REQ-031,
 *            ODE-PRD-REQ-073, ODE-PRD-REQ-076, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type CheckpointTimeline,
  type CheckpointTimelineResult,
} from '../../api/client.js';

/** DO id wire contract (mirror of the server UUID check). */
export const DO_ID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export type CheckpointTimelineState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly doId: string }
  | { readonly kind: 'ready'; readonly timeline: CheckpointTimeline }
  | { readonly kind: 'empty'; readonly doId: string }
  | { readonly kind: 'not_found'; readonly doId: string }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseCheckpointTimelineOptions {
  readonly token: string | null;
  /** Test seam: timeline fetch (default: the shared api client). */
  readonly fetch?:
    | ((doId: string, token: string) => Promise<CheckpointTimelineResult>)
    | undefined;
}

export interface UseCheckpointTimelineResult {
  readonly doId: string;
  readonly setDoId: (value: string) => void;
  readonly submit: () => void;
  readonly reset: () => void;
  readonly state: CheckpointTimelineState;
}

export function useCheckpointTimeline(
  options: UseCheckpointTimelineOptions,
): UseCheckpointTimelineResult {
  const { token } = options;
  const [doId, setDoIdState] = useState('');
  const [state, setState] = useState<CheckpointTimelineState>({ kind: 'idle' });
  const [request, setRequest] = useState<{ readonly attempt: number; readonly doId: string } | null>(
    null,
  );
  const attemptRef = useRef(0);

  const fetchRef = useRef(
    options.fetch ??
      ((value: string, tokenValue: string) => api.fetchCheckpointTimeline(value, tokenValue)),
  );
  fetchRef.current =
    options.fetch ??
    ((value: string, tokenValue: string) => api.fetchCheckpointTimeline(value, tokenValue));

  const setDoId = useCallback((value: string) => {
    setDoIdState(value);
  }, []);

  const submit = useCallback(() => {
    const trimmed = doId.trim();
    if (!DO_ID_PATTERN.test(trimmed)) {
      setState({ kind: 'error', message: 'ID Delivery Order tidak valid (harus UUID).' });
      return;
    }
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, doId: trimmed });
  }, [doId]);

  const reset = useCallback(() => {
    setDoIdState('');
    setRequest(null);
    setState({ kind: 'idle' });
  }, []);

  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading', doId: request.doId });
    fetchRef
      .current(request.doId, token)
      .then((result) => {
        if (cancelled) return;
        setState(mapTimelineResult(result, request.doId));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Kesalahan jaringan',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, request]);

  return { doId, setDoId, submit, reset, state };
}

/** Maps the guarded fetch outcome to a REQ-090 state (never a false success). */
export function mapTimelineResult(
  result: CheckpointTimelineResult,
  doId: string,
): CheckpointTimelineState {
  switch (result.status) {
    case 200:
      if (result.payload === null) {
        return { kind: 'error', message: 'Respons linimasa tidak valid.' };
      }
      if (result.payload.checkpoints.length === 0) return { kind: 'empty', doId };
      return { kind: 'ready', timeline: result.payload };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Akses ditolak.' };
    case 404:
      return { kind: 'not_found', doId };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}
